export type MidiNoteCallback = (note: number, velocity: number, channel: number, deviceId: string) => void;
export type MidiStatusCallback = (devices: MidiDeviceInfo[]) => void;
export type MidiExpressionCallback = (channel: number, value: number, deviceId: string) => void;

export type MidiChannelMode = 'omni' | 'chPerNote' | 'chPerRow';

export interface MidiDeviceInfo {
  id: string;
  name: string;
  manufacturer: string;
  enabled: boolean;
  connected: boolean;
}
export class MidiInput {
  private access: MIDIAccess | null = null;

  private inputs = new Map<string, { input: MIDIInput; enabled: boolean }>();

  private noteOnCallbacks: MidiNoteCallback[] = [];
  private noteOffCallbacks: MidiNoteCallback[] = [];
  private statusCallbacks: MidiStatusCallback[] = [];
  private pitchBendCallbacks: MidiExpressionCallback[] = [];
  private slideCallbacks: MidiExpressionCallback[] = [];
  private pressureCallbacks: MidiExpressionCallback[] = [];

  private channelMode: MidiChannelMode = 'omni';
  private pressureCC: number | null = null;

  private _available = false;

  get isAvailable(): boolean { return this._available; }

  get overallStatus(): 'unavailable' | 'no-devices' | 'connected' {
    if (!this._available) return 'unavailable';
    const connected = [...this.inputs.values()].filter(d => d.enabled);
    return connected.length > 0 ? 'connected' : 'no-devices';
  }

  get connectedDeviceName(): string {
    return [...this.inputs.values()]
      .filter(d => d.enabled)
      .map(d => d.input.name ?? 'Unknown')
      .join(', ');
  }
  async init(): Promise<void> {
    if (!('requestMIDIAccess' in navigator)) {
      this._available = false;
      this.notifyStatus();
      return;
    }
    try {
      this.access = await navigator.requestMIDIAccess({ sysex: false });
      this._available = true;
      this.access.onstatechange = () => { this.rescan(); };
      this.rescan();
    } catch {
      this._available = false;
      this.notifyStatus();
    }
  }
  private rescan(): void {
    if (!this.access) return;

    const currentIds = new Set(this.access.inputs.keys());

    for (const [id, { input }] of this.inputs) {
      if (!currentIds.has(id)) {
        input.onmidimessage = null;
        this.inputs.delete(id);
      }
    }

    for (const [id, input] of this.access.inputs) {
      if (!this.inputs.has(id)) {
        input.onmidimessage = (e) => { this.handleMessage(id, e); };
        this.inputs.set(id, { input, enabled: true });
      } else {
        const entry = this.inputs.get(id);
        if (!entry) continue;
        entry.input.onmidimessage = entry.enabled
          ? (e) => { this.handleMessage(id, e); }
          : null;
      }
    }

    this.notifyStatus();
  }
  setDeviceEnabled(id: string, enabled: boolean): void {
    const entry = this.inputs.get(id);
    if (!entry) return;
    entry.enabled = enabled;
    entry.input.onmidimessage = enabled
      ? (e) => { this.handleMessage(id, e); }
      : null;
    this.notifyStatus();
  }

  getDevices(): MidiDeviceInfo[] {
    return [...this.inputs.entries()].map(([id, { input, enabled }]) => ({
      id,
      name: input.name ?? 'Unknown device',
      manufacturer: input.manufacturer ?? '',
      enabled,
      connected: input.state === 'connected',
    }));
  }
  setChannelMode(mode: MidiChannelMode): void {
    this.channelMode = mode;
  }

  getChannelMode(): MidiChannelMode {
    return this.channelMode;
  }

  setPressureCC(cc: number | null): void {
    this.pressureCC = cc;
  }
  private handleMessage(deviceId: string, event: MIDIMessageEvent): void {
    const entry = this.inputs.get(deviceId);
    if (!entry?.enabled) return;

    const data = event.data;
    if (!data || data.length < 2) return;

    const statusByte = data[0];
    const type = statusByte & 0xF0;
    const channel = (statusByte & 0x0F) + 1; // 1-indexed
    const note = data[1];
    const velocity = data.length > 2 ? data[2] : 0;

    if (type === 0x90 && velocity > 0) {
      for (const cb of this.noteOnCallbacks) cb(note, velocity, channel, deviceId);
    } else if (type === 0x80 || (type === 0x90 && velocity === 0)) {
      for (const cb of this.noteOffCallbacks) cb(note, velocity, channel, deviceId);
    } else if (type === 0xE0) {
      const raw = (data[2] << 7) | data[1];
      const normalized = raw / 8191.5 - 1; // range -1..+1
      for (const cb of this.pitchBendCallbacks) cb(channel, normalized, deviceId);
    } else if (type === 0xB0 && (note === 74 || note === 1 || note === 11)) {
      const normalized = velocity / 127;
      if (this.pressureCC !== null && note === this.pressureCC) {
        for (const cb of this.pressureCallbacks) cb(channel, normalized, deviceId);
      } else {
        for (const cb of this.slideCallbacks) cb(channel, normalized, deviceId);
      }
    } else if (type === 0xA0) {
      if (data.length < 3) return;
      const normalized = data[2] / 127;
      for (const cb of this.pressureCallbacks) cb(channel, normalized, deviceId);
    } else if (type === 0xD0) {
      const normalized = data[1] / 127;
      for (const cb of this.pressureCallbacks) cb(channel, normalized, deviceId);
    }
  }
  onNoteOn(cb: MidiNoteCallback): void { this.noteOnCallbacks.push(cb); }
  onNoteOff(cb: MidiNoteCallback): void { this.noteOffCallbacks.push(cb); }

  onStatusChange(cb: MidiStatusCallback): void { this.statusCallbacks.push(cb); }

  onPitchBend(cb: MidiExpressionCallback): void { this.pitchBendCallbacks.push(cb); }
  onSlide(cb: MidiExpressionCallback): void { this.slideCallbacks.push(cb); }
  onPressure(cb: MidiExpressionCallback): void { this.pressureCallbacks.push(cb); }

  removeNoteOn(cb: MidiNoteCallback): void {
    this.noteOnCallbacks = this.noteOnCallbacks.filter(c => c !== cb);
  }
  removeNoteOff(cb: MidiNoteCallback): void {
    this.noteOffCallbacks = this.noteOffCallbacks.filter(c => c !== cb);
  }
  removeStatusChange(cb: MidiStatusCallback): void {
    this.statusCallbacks = this.statusCallbacks.filter(c => c !== cb);
  }

  private notifyStatus(): void {
    const devices = this.getDevices();
    for (const cb of this.statusCallbacks) cb(devices);
  }

  dispose(): void {
    for (const { input } of this.inputs.values()) {
      input.onmidimessage = null;
    }
    this.inputs.clear();
  }
}
